import { AudienceSegmentSelect } from '@/components/atoms';
import { abbreviateNumber } from '@/helpers';
import { IAudienceInsight, IAudienceSegment } from '@/interfaces';
import { formatPercentage } from '@/utils';
import {
  Female,
  Group,
  LocationOn,
  Male,
  PeopleOutline,
  Person,
} from '@mui/icons-material';
import { Box, Paper, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import React, { useMemo } from 'react';
import { DemographicCard } from './DemographicCard';
import { KeyInterests } from './KeyInterests';
import { TopAccounts } from './TopAccounts';

type SummaryViewProps = {
  insight: IAudienceInsight;
  selectedSegment: IAudienceSegment;
  onSegmentChange: (newValue: number) => void;
};

export const SummaryView: React.FC<SummaryViewProps> = ({
  insight,
  selectedSegment,
  onSegmentChange,
}) => {
  const { segments } = insight;

  const topAge = useMemo(
    () =>
      selectedSegment.summary.demographics
        .filter((score) => score.type === 'Age')
        .reduce((prev, current) =>
          prev.index > current.index ? prev : current
        ),
    [selectedSegment]
  );

  const topGender = useMemo(
    () =>
      selectedSegment.summary.demographics
        .filter((score) => score.type === 'Gender')
        .reduce((prev, current) =>
          prev.penetration > current.penetration ? prev : current
        ),
    [selectedSegment]
  );

  const topInterests = useMemo(
    () =>
      [...selectedSegment.score.interests]
        .sort((a, b) => b.index - a.index)
        .slice(0, 6),
    [selectedSegment]
  );

  return (
    <Grid xs={12}>
      {/* Audience name and segment dropdown */}
      <Grid xs={12} mb={2}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <Typography variant="h4">{insight.audience_name}</Typography>
          {segments && segments.length > 0 && (
            <AudienceSegmentSelect
              segments={segments}
              selectedId={selectedSegment.id}
              onChange={onSegmentChange}
            />
          )}
        </Box>
      </Grid>

      {/* Audience image & size section */}
      <Grid xs={12} container spacing={3} mb={2}>
        <Grid
          xs={12}
          md={3}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          gap={3}
        >
          {/* image/avatar */}
          <Paper
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              padding: 2,
              width: 120,
              height: 120,
            }}
          >
            <PeopleOutline
              fontSize="large"
              sx={{
                fontSize: '6rem',
                color: 'white',
              }}
            />
          </Paper>
          {/* audience size */}
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: 2,
              minWidth: 170,
              width: '100%',
            }}
          >
            <Stack
              direction="row"
              spacing={3}
              alignItems="center"
              width="100%"
              justifyContent="center"
            >
              <DemographicCard
                icon={<Person sx={{ color: 'secondary.main', mb: 0.5 }} />}
                label="Social Size"
                value={abbreviateNumber(
                  selectedSegment.summary.audience_size.social_size
                )}
                color="primary.light"
              />
              <DemographicCard
                icon={<Group sx={{ color: 'secondary.main', mb: 0.5 }} />}
                label="Modelled Size"
                value={abbreviateNumber(
                  selectedSegment.summary.audience_size.modelled_size
                )}
                color="primary.light"
              />
            </Stack>
          </Paper>
        </Grid>

        {/* Key Demographics & Interests Section */}
        <Grid xs={12} md={9} container spacing={3}>
          <Grid xs={12} md={4}>
            <Paper
              sx={{
                padding: 3,
                flex: 1,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography variant="h6" mb={4} fontWeight={700}>
                Key Demographics
              </Typography>
              <Stack
                direction="row"
                spacing={4}
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <DemographicCard
                  icon={
                    topGender.name === 'male' ? (
                      <Male sx={{ fontSize: 32, color: 'secondary.dark' }} />
                    ) : (
                      <Female sx={{ fontSize: 32, color: 'secondary.dark' }} />
                    )
                  }
                  label="Gender"
                  value={
                    topGender.name.charAt(0).toUpperCase() +
                    topGender.name.slice(1)
                  }
                  percentage={formatPercentage(topGender.penetration)}
                  color="secondary.light"
                />
                <DemographicCard
                  icon={
                    <Group sx={{ fontSize: 32, color: 'secondary.dark' }} />
                  }
                  label="Age"
                  value={topAge.name}
                  percentage={formatPercentage(topAge.penetration)}
                  color="primary.light"
                />
                <DemographicCard
                  icon={
                    <LocationOn
                      sx={{ fontSize: 32, color: 'secondary.dark' }}
                    />
                  }
                  label="Location"
                  value={insight.country_iso}
                  color="primary.light"
                />
              </Stack>
            </Paper>
          </Grid>
          <Grid
            xs={12}
            md={8}
            sx={{
              height: '100%',
            }}
          >
            <KeyInterests interests={topInterests} />
          </Grid>
        </Grid>
      </Grid>

      {/* Audience description */}
      <Grid xs={12} mb={2}>
        <Typography variant="h5" fontWeight={700} mb={2}>
          Audience Description
        </Typography>
        <Paper
          sx={{
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography variant="body2">
            <span
              dangerouslySetInnerHTML={{
                __html:
                  selectedSegment.summary.description ??
                  selectedSegment.description ??
                  '',
              }}
            />
          </Typography>
        </Paper>
      </Grid>

      {/* Brands grouped by interest */}
      <TopAccounts interests={topInterests} />
    </Grid>
  );
};
